(() => {
if (customElements.get('sam-product')) return;
/* Progressive enhancement: native product form, media links and disclosures work without JS. */
class SamProduct extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.track = this.querySelector('[data-media-track]');
    this.slides = [...this.querySelectorAll('[data-media-index]')];
    this.currentIndex = 0;
    this.autoplayPaused = false;
    this.mobileMedia = matchMedia('(max-width: 768px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.restorePurchase?.(); }, options);
    this.addEventListener('click', (event) => this.onClick(event), options);
    this.addEventListener('change', (event) => this.onChange(event), options);
    this.addEventListener('submit', (event) => this.onSubmit(event), options);
    this.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.showMedia(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    this.querySelector('[data-gallery-controls]')?.removeAttribute('hidden');
    this.querySelector('[data-mobile-gallery-controls]')?.removeAttribute('hidden');
    this.mobileMedia.addEventListener('change', () => this.startAutoplay(), options);
    this.reducedMotion.addEventListener('change', () => this.startAutoplay(), options);
    document.addEventListener('visibilitychange', () => this.startAutoplay(), options);
    this.querySelector('[data-gallery]')?.addEventListener('pointerdown', () => this.stopAutoplay(), options);
    this.querySelector('[data-gallery]')?.addEventListener('pointerup', () => this.startAutoplay(), options);
    this.querySelector('[data-gallery]')?.addEventListener('focusin', () => this.stopAutoplay(), options);
    this.querySelector('[data-gallery]')?.addEventListener('focusout', () => this.startAutoplay(), options);
    this.querySelector('.sam-product-gallery__dialog')?.addEventListener('close', () => this.startAutoplay(), options);
    this.track?.addEventListener('scroll', () => this.updateMediaIndex(), { ...options, passive: true });
    this.updateMediaIndex();
    this.enhancePurchase();
    this.addEventListener('shopify:block:select', (event) => {
      const detail = event.target.closest('details');
      if (detail) detail.open = true;
    }, options);
    const featured = this.querySelector('[data-featured]');
    if (featured) this.showMedia(Number(featured.dataset.mediaIndex), false);
    this.galleryObserver = new IntersectionObserver(([entry]) => {
      this.galleryVisible = entry.isIntersecting;
      this.startAutoplay();
    }, { threshold: .3 });
    if (this.track) this.galleryObserver.observe(this.track);
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.variantAbort?.abort();
    this.mediaObserver?.disconnect();
    this.buyObserver?.disconnect();
    this.galleryObserver?.disconnect();
    this.stopAutoplay();
  }
  stopAutoplay() { clearInterval(this.autoplayTimer); }
  startAutoplay() {
    this.stopAutoplay();
    const button = this.querySelector('[data-gallery-autoplay]');
    const paused = this.autoplayPaused || this.reducedMotion.matches;
    button?.setAttribute('aria-label', paused ? 'Start automatic slideshow' : 'Pause automatic slideshow');
    if (button) button.innerHTML = paused
      ? '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="m5 3 8 5-8 5Z" fill="currentColor"/></svg>'
      : '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M5 3v10M11 3v10" stroke="currentColor" stroke-width="2"/></svg>';
    if (this.dataset.galleryAutoplayEnabled === 'false' || window.Shopify?.designMode || paused || !this.mobileMedia.matches || document.hidden || !this.galleryVisible || this.slides.length < 2) return;
    this.autoplayTimer = setInterval(() => {
      if (this.querySelector('.sam-product-gallery__dialog[open]')) return;
      if (this.querySelector('[data-gallery]').contains(document.activeElement) && document.activeElement !== button) return;
      this.showMedia(this.currentIndex + 1);
    }, 2500);
  }
  enhancePurchase() {
    this.querySelectorAll('[data-quantity-step]').forEach((button) => { button.hidden = false; });
    this.buyObserver?.disconnect();
    const purchase = this.querySelector('[data-purchase]');
    const bar = this.querySelector('[data-sticky-buy]');
    if (purchase && bar && purchase.dataset.stickyEnabled === 'true') {
      this.buyObserver = new IntersectionObserver(([entry]) => { bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0; }, { threshold: 0 });
      this.buyObserver.observe(purchase);
    }
    this.syncPackage();
  }
  syncPackage() {
    const option = this.querySelector('[data-package]:checked');
    if (option) this.querySelector('[data-price]').textContent = option.dataset.packagePrice;
    const purchaseTotal = this.querySelector('[data-purchase-total]');
    if (purchaseTotal && option) purchaseTotal.textContent = option.dataset.stickyPrice;
    const form = this.querySelector('[data-sam-product-form]');
    if (form?.dataset.singleCheckoutUrl && option) {
      form.dataset.checkoutUrl = option.dataset.checkoutUrl || (option.value === '2' ? form.dataset.bundleCheckoutUrl : form.dataset.singleCheckoutUrl);
      const returnTo = form.querySelector('[name="return_to"]');
      if (returnTo && form.dataset.purchaseAction === 'checkout') returnTo.value = form.dataset.checkoutUrl;
    }
    const prices = option?.dataset.stickyPrice ? option.dataset : this.querySelector('[data-purchase]')?.dataset;
    const sticky = this.querySelector('[data-sticky-buy] [data-sticky-price]');
    const compare = this.querySelector('[data-sticky-buy] [data-sticky-compare]');
    if (sticky && prices) sticky.textContent = prices.stickyPrice;
    if (compare && prices) {
      compare.textContent = prices.stickyCompare || '';
      compare.hidden = !prices.stickyCompare;
    }
  }
  updateMediaIndex() {
    if (!this.track?.clientWidth) return;
    this.currentIndex = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
    this.querySelectorAll('[data-gallery-thumb], [data-gallery-dot]').forEach((control) => {
      const selected = Number(control.dataset.galleryThumb ?? control.dataset.galleryDot) === this.currentIndex;
      if (selected) control.setAttribute('aria-current', 'true');
      else control.removeAttribute('aria-current');
    });
    const counter = this.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = this.currentIndex + 1;
    this.slides.forEach((slide, i) => { if (i !== this.currentIndex) slide.querySelectorAll('video').forEach((video) => video.pause()); });
  }
  showMedia(index, animate = true) {
    if (!this.track || !this.slides.length) return;
    index = (index + this.slides.length) % this.slides.length;
    this.track.scrollTo({ left: this.track.clientWidth * index, behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
  }
  onClick(event) {
    const autoplay = event.target.closest('[data-gallery-autoplay]');
    if (autoplay) { this.autoplayPaused = !this.autoplayPaused; this.startAutoplay(); return; }
    const dot = event.target.closest('[data-gallery-dot]');
    if (dot) { this.showMedia(Number(dot.dataset.galleryDot)); this.startAutoplay(); return; }
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) { event.preventDefault(); this.showMedia(Number(thumb.dataset.galleryThumb)); return; }
    const step = event.target.closest('[data-gallery-step]');
    if (step) { this.showMedia(this.currentIndex + Number(step.dataset.galleryStep)); this.startAutoplay(); return; }
    const zoom = event.target.closest('[data-image-zoom]');
    if (zoom) {
      event.preventDefault();
      const dialog = this.querySelector('.sam-product-gallery__dialog');
      const image = dialog.querySelector('[data-zoom-image]');
      image.src = zoom.href;
      image.alt = zoom.querySelector('img').alt;
      dialog.showModal();
      this.stopAutoplay();
      return;
    }
    const quantityButton = event.target.closest('[data-quantity-step]');
    if (quantityButton) {
      const input = this.querySelector('[name="quantity"]');
      if (Number(quantityButton.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const jump = event.target.closest('[data-buy-jump]');
    if (jump) {
      event.preventDefault();
      const purchase = this.querySelector('[data-purchase]');
      purchase.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      this.querySelector('[data-add-to-cart]')?.focus({ preventScroll: true });
    }
  }
  async onChange(event) {
    if (this.adding) return;
    if (event.target.matches('[data-package]')) { this.syncPackage(); return; }
    const variantChanged = event.target.matches('[data-variant-select]');
    const planChanged = event.target.matches('[data-plan-select]');
    if (!variantChanged && !planChanged) return;
    const id = this.querySelector('[data-variant-select]').value;
    const plan = this.querySelector('[data-plan-select]')?.value;
    const quantity = this.querySelector('[data-package]:checked, input[type="number"][name="quantity"]')?.value;
    this.variantAbort?.abort();
    this.variantAbort = new AbortController();
    const url = new URL(this.dataset.featuredProduct === 'true' ? location.href : this.dataset.productUrl, location.origin);
    url.searchParams.set('variant', id);
    if (plan) url.searchParams.set('selling_plan', plan);
    url.searchParams.set('section_id', this.dataset.sectionId);
    const purchase = this.querySelector('[data-purchase]');
    purchase.setAttribute('aria-busy', 'true');
    this.querySelector('[data-add-to-cart]').disabled = true;
    try {
      const response = await fetch(url, { signal: this.variantAbort.signal });
      if (!response.ok) throw new Error('Unable to load this option.');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = doc.querySelector('[data-purchase]');
      if (!replacement) throw new Error('Unable to load this option.');
      // Keep app-supplied line item properties, including file input selections.
      purchase.querySelectorAll('[name^="properties["]').forEach(input => {
        const matching = [...replacement.querySelectorAll('[name]')].find(node => node.name === input.name && node.value === input.value);
        if (matching) matching.replaceWith(input);
        else replacement.querySelector('form').append(input);
      });
      purchase.replaceWith(replacement);
      const quantityOption = [...replacement.querySelectorAll('[data-package]')].find(input => input.value === quantity && !input.disabled);
      if (quantityOption) quantityOption.checked = true;
      const quantityInput = replacement.querySelector('input[type="number"][name="quantity"]');
      if (quantityInput && quantity) {
        quantityInput.value = quantity;
        if (!quantityInput.checkValidity()) quantityInput.value = quantityInput.min || '1';
      }
      const pageURL = new URL(location.href);
      pageURL.searchParams.set('variant', id);
      const selectedPlan = replacement.querySelector('[data-plan-select]')?.value;
      if (selectedPlan) pageURL.searchParams.set('selling_plan', selectedPlan);
      else pageURL.searchParams.delete('selling_plan');
      history.replaceState({}, '', pageURL);
      this.enhancePurchase();
      this.querySelector(planChanged ? '[data-plan-select]' : '[data-variant-select]')?.focus();
      const mediaIndex = this.slides.findIndex(slide => slide.dataset.mediaId === replacement.dataset.variantMedia);
      if (variantChanged && mediaIndex >= 0) this.showMedia(mediaIndex);
      window.Shopify?.PaymentButton?.init?.();
      const variant = JSON.parse(replacement.querySelector('[data-selected-variant]').textContent);
      document.dispatchEvent(new CustomEvent('variant:update', { bubbles: true, detail: { resource: variant, sourceId: this.id, data: { html: doc, productId: String(variant.product_id || this.dataset.productId) } } }));
      document.dispatchEvent(new CustomEvent('sam:variant-change', { detail: { variantId: id, sectionId: this.dataset.sectionId } }));
    } catch (err) {
      if (err.name !== 'AbortError') {
        const fallback = new URL(location.href);
        fallback.searchParams.set('variant', id);
        if (plan) fallback.searchParams.set('selling_plan', plan);
        else fallback.searchParams.delete('selling_plan');
        location.assign(fallback);
      }
    }
  }
  async onSubmit(event) {
    const form = event.target.closest('[data-sam-product-form]');
    if (!form || (event.submitter && event.submitter.name !== 'add') || !window.SamShop) return;
    event.preventDefault();
    if (this.adding || !form.reportValidity() || form.closest('[aria-busy="true"]')) return;
    this.adding = true;
    const button = form.querySelector('[data-add-to-cart]');
    const label = button.querySelector('[data-add-label]');
    const error = form.querySelector('[data-product-error]');
    const originalLabel = label.textContent;
    error.textContent = '';
    const data = new FormData(form);
    data.append('sections', 'sam-cart-content');
    data.append('sections_url', location.pathname);
    button.disabled = true;
    label.textContent = 'Adding…';
    form.setAttribute('aria-busy', 'true');
    const options = [...form.querySelectorAll('[name="id"], [name="quantity"], [name="selling_plan"]')];
    const disabledStates = options.map(input => input.disabled);
    options.forEach(input => { input.disabled = true; });
    this.restorePurchase = () => {
      this.adding = false;
      options.forEach((input, index) => { input.disabled = disabledStates[index]; });
      button.disabled = false;
      label.textContent = originalLabel;
      form.removeAttribute('aria-busy');
    };
    let added = false;
    try {
      const response = await fetch(`${document.body.dataset.cartAddUrl}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Sam could not be added. Please try again.');
      added = true;
      if (form.dataset.purchaseAction === 'checkout') {
        label.textContent = 'Opening checkout…';
        location.assign(form.dataset.checkoutUrl);
        return;
      }
      let count;
      if (cart.sections?.['sam-cart-content']) count = window.SamShop.renderCart(cart.sections['sam-cart-content']);
      else count = await window.SamShop.refreshCart();
      window.SamShop.publishCart(cart, count, this.id);
      window.SamShop.announce('Added to your cart.');
      const dialog = document.getElementById('SamCart');
      dialog.querySelector('[data-cart-error]').textContent = '';
      if (!dialog.open) dialog.showModal();
    } catch (err) {
      if (added) { location.href = document.body.dataset.cartUrl; return; }
      error.textContent = err.message || 'Check your connection and try again.';
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { sourceId: this.id, message: error.textContent } }));
    } finally {
      if (added && form.dataset.purchaseAction === 'checkout') return;
      this.restorePurchase();
    }
  }
}
if (!customElements.get('sam-product')) customElements.define('sam-product', SamProduct);

})();
